<template>
  <el-dialog
    id="responseModal"
    title="View response"
    :visible.sync="showLocal"
    :width="dialogWidth"
  >
    <div id="pulsePage">
      <!-- Responsive layout -->
      <div
        :class="['desktop', 'laptop'].includes($mq) ? 'pageDesk' : 'pageMobile'"
      >
        <div
          v-for="(pageData, pageKey) in pageStructure"
          :key="pageKey"
        >
          <div
            :key="pageKey"
            class="eachPage"
          >
            <!-- Description with leader data -->
            <div class="description">
              <span
                v-for="(desc, descKey) in pageData.desc.leader"
                :key="descKey"
              >
                {{ desc }}
              </span>
            </div>

            <!-- Question list divide by type -->
            <div class="questionWrap">
              <!-- LGA Step type -->
              <div
                v-if="pageData.type === 'step.lga' && pageSource"
                class="questionSelect lgaStep"
              >
                <!-- Desk mode = step -->
                <el-steps
                  v-if="['desktop', 'laptop'].includes($mq)"
                  :active="pageSource.answer[`question${pageData.question}`] === 0 ?
                    5 : pageSource.answer[`question${pageData.question}`] - 1"
                  align-center
                >
                  <el-step
                    v-for="(item, stepKey) in pageSource.step.lga"
                    :id="'customStep' + item.score"
                    :key="stepKey"
                    :title="item.desc"
                    :class="{noFollowingLine: item.score === 5}"
                  >
                    <template
                      slot="icon"
                    >
                      <button class="iconButton">
                        <span v-if="item.score === 0">
                          NN
                        </span>
                        <span v-else>
                          {{ item.score }}
                        </span>
                      </button>
                    </template>
                  </el-step>
                </el-steps>

                <!-- Mobile mode = radio -->
                <el-radio-group
                  v-else
                  v-model="pageSource.answer[`question${pageData.question}`]"
                  disabled
                >
                  <el-radio-button
                    v-for="(item, key) in pageSource.step.lga"
                    :key="key"
                    :label="item.score"
                  >
                    <div class="radioScore">
                      <i
                        v-if="item.score > 3"
                        class="el-icon-check"
                      ></i>
                      <span v-else>
                        {{ item.score }}
                      </span>
                    </div>
                    <div class="radioDesc">
                      {{ item.desc }}
                    </div>
                  </el-radio-button>
                </el-radio-group>
              </div>
              <!-- /LGA step type -->

              <!-- Plan step and leadership type -->
              <div
                v-else-if="(pageData.type === 'step.plan' || pageData.type === 'step.leadership')
                  && pageSource"
                class="questionSelect"
              >
                <el-steps
                  v-if="['desktop', 'laptop'].includes($mq)"
                  :active="pageSource.answer[`question${pageData.question}`] - 1"
                  align-center
                >
                  <!-- Deskmode = step -->
                  <el-step
                    v-for="(item, stepKey) in pageSource.step.plan"
                    :id="'customStep' + item.score"
                    :key="stepKey"
                    :title="item.desc"
                    :class="{
                      noFollowingLine: item.score === 5,
                      hide: item.score === 0
                    }"
                  >
                    <template slot="icon">
                      <button class="iconButton">
                        {{ item.score }}
                      </button>
                    </template>
                  </el-step>
                </el-steps>

                <!-- Mobile mode = radio -->
                <el-radio-group
                  v-else
                  v-model="pageSource.answer[`question${pageData.question}`]"
                  disabled
                >
                  <el-radio-button
                    v-for="(item, key) in pageSource.step.plan"
                    :key="key"
                    :label="item.score"
                  >
                    <div class="radioScore">
                      <i class="el-icon-check"></i>
                    </div>
                    <div class="radioDesc">
                      {{ item.desc }}
                    </div>
                  </el-radio-button>
                </el-radio-group>
              </div>
              <!-- /Plan step and leadership type -->

              <!-- Text area type -->
              <div
                v-else-if="pageData.type === 'textarea' && pageSource"
                class="questionSelect"
              >
                <div class="questionText">
                  <el-input
                    v-model="pageSource.answer[`question${pageData.question}`]"
                    class="questionArea"
                    type="textarea"
                    placeholder=""
                    maxlength="3000"
                    rows="8"
                    autosize
                    show-word-limit
                  />
                  <div class="textBarrier"></div>
                </div>
              </div>
              <!-- /Text area type -->
            </div>
          </div>
        </div>
      </div>
      <!-- /Responsive layout -->
    </div>
  </el-dialog>
</template>

<script>
import api from '@/api';
import dialog from '@/mixins/dialog';

export default {
  name: 'ResponseModal',

  mixins: [dialog],

  props: {
    selectedSurvey: {
      type: String,
      default: null,
    },

    activatedPulse: {
      type: Number,
      default: 1,
    },
  },

  data() {
    return {
      report: null,

      currentPage: 1,
      pageSource: {
      // Unanswered first page
        blank: 1,
        blankList: [],
        firstTry: true,
        // Select Step key, value
        step: {
        // Step value not string title
          key: {
            q1a: -1,
            q1b: -1,
            q2: -1,
          },
          // LGA Steps data
          lga: [
            {
              score: 1,
              desc: 'Not at all satisfied',
            },
            {
              score: 2,
              desc: 'Slightly satisfied',
            },
            {
              score: 3,
              desc: 'Moderately satisfied',
            },
            {
              score: 4,
              desc: 'Very satisfied',
            },
            {
              score: 5,
              desc: 'Completely satisfied',
            },

            {
              score: 0,
              desc: 'No improvement needed',
            },
          ],
          // Plan Steps data
          plan: [
            {
              score: 1,
              desc: 'Not at all satisfied',
            },
            {
              score: 2,
              desc: 'Slightly satisfied',
            },
            {
              score: 3,
              desc: 'Moderately satisfied',
            },
            {
              score: 4,
              desc: 'Very satisfied',
            },
            {
              score: 5,
              desc: 'Completely satisfied',
            },
            {
              score: 0,
              desc: 'No improvement needed',
            },
          ],

          leadership: [
            {
              score: 1,
              desc: 'Not at all satisfied',
            },
            {
              score: 2,
              desc: 'Slightly satisfied',
            },
            {
              score: 3,
              desc: 'Moderately satisfied',
            },
            {
              score: 4,
              desc: 'Very satisfied',
            },
            {
              score: 5,
              desc: 'Completely satisfied',
            },

            {
              score: 0,
              desc: 'No improvement needed',
            },
          ],
        },
        // Answer results
        answer: {
          q1a: -10,
          q1b: -10,
          q2: -10,
          q3: -10,
          q4: '',
          q5: '',
          q6: '',
        },
      },
      pageStructure: [
        {
          page: 1,
          question: '1a',
          title: 'A. Leadership Growth Area 1',
          type: 'step.lga',
          desc: {
            leader: ['From then until now, how satisfied are you with your improvement in the following leadership behavior:'],
            stakeholder: ['From then until now, how satisfied are you with their improvement in the following leadership behavior:'],
          },
        },
        {
          page: 2,
          question: '1b',
          title: 'B. Leadership Growth Area 2',
          type: 'step.lga',
          desc: {
            leader: ['From then until now, how satisfied are you with your improvement in the following leadership behavior:'],
            stakeholder: ['From then until now, how satisfied are you with their improvement in the following leadership behavior:'],
          },
        },
        {
          page: 3,
          question: '2',
          type: 'step.plan',
          desc: {
            leader: ['From then until now, how satisfied are you with your implementation of your action plan?'],
            stakeholder: ['From then until now, how satisfied are you with their implementation of their action plan?'],
          },
        },
        {
          page: 4,
          question: '3',
          type: 'step.leadership',
          desc: {
            leader: ['From then until now, how satisfied are you with your improvement in overall leadership effectiveness?'],
            stakeholder: ['From then until now, how satisfied are you with their improvement in overall leadership effectiveness?'],
          },
        },
        {
          page: 5,
          question: '4',
          type: 'textarea',
          desc: {
            leader: ['From then until now, what have you done in your leadership growth areas to become a more effective leader and what did you find particularly effective? You can add comments in your preferred language:'],
            stakeholder: [
              'From then until now, what has',
              'done in their leadership growth areas to become a more effective leader and what did you find particularly effective? You can add comments in your preferred language:',
            ],
          },
        },
        {
          page: 6,
          question: '5',
          type: 'textarea',
          desc: {
            leader: ['What can you do in your leadership growth areas to become more effective as a leader in the next 3-4 months? You can add comments in your preferred language:'],
            stakeholder: [
              'What can',
              'do in their leadership growth areas to become more effective as a leader in the next 3-4 months? You can add comments in your preferred language:',
            ],
          },
        },
        {
          page: 7,
          question: '6',
          type: 'textarea',
          desc: {
            leader: ['Are there any additional comments you would like to share? You can add comments in your preferred language:'],
            stakeholder: [
              'Are there any additional comments you would like to share with',
              '? You can add comments in your preferred language:',
            ],
          },
        },
      ],
    };
  },

  computed: {
    showLocal: {
      get() {
        return this.show;
      },
      set(newValue) {
        this.$emit('update:show', newValue);
        if (!newValue) {
          this.$emit('update:selectedSurvey', null);
        }
      },
    },
  },

  watch: {
    selectedSurvey(newValue) {
      if (newValue) {
        this.getSurvey();
      } else {
        this.pageSource.answer = {
          question1a: -10,
          question1b: -10,
          question2: -10,
          question3: '',
          question4: '',
          question5: '',
        };
      }
    },
  },

  methods: {
    async getSurvey() {
      try {
        const response = await api.getPulseResponse(this.selectedSurvey);
        this.pageSource.answer = response.data;
      } catch (err) {
        console.log(err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  #pulsePage {
    .pageDesk {
      position: relative;

      .VueCarousel {
        padding-bottom: 40px;
      }

      .VueCarousel-slide {
        padding: 5px 10px;
        box-sizing: border-box;
      }

      .sceneCounter {
        font-size: 16px;
        color: black;
        line-height: 17px;
      }

      .description {
        font-size: 15px;
        padding: 25px 0 5px;

        &.redAlert {
          color: red;
        }
      }

      .questionWrap {
        padding: 15px 0;

        .questionTitle {
          padding: 10px 0;

          &.redAlert {
            color: red;
          }
        }

        .questionSelect {
          .el-step {
            width: 10%;
            padding: 15px 0 10px;

            &.hide {
              display: none;
            }

            .iconButton {
              background: transparent;
              border: none;
              width: 100%;
              height: 100%;
              border-radius: 100%;
              font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
              font-size: 15px;
              color: #9c9c9c;
              cursor: pointer;
              padding: 0;

              &:focus {
                outline: none;
              }

              .iconSymbol {
                width: 100%;
                font-weight: bold;
                font-size: 18px;
                margin-top: 2px;
              }
            }
          }
        }

        .questionText {
          position: relative;

          .questionArea {
            position: relative;
            top: 0;
            left: 0;
            border-radius: 5px;
            box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
          }

          .textButton {
            position: absolute;
            width: 60px;
            font-weight: bold;
            background: white;
            bottom: 0;
            right: 0;
          }

          .textBarrier {
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
          }
        }
      }

      .customPagination {
        position: absolute;
        width: 100%;
        bottom: -20px;
      }
    }

    .pageMobile {
      width: 100%;
      height: 100%;
      overflow: auto;
      background: white;

      .VueCarousel-slide {
        padding: 5px 10px;
        box-sizing: border-box;
      }

      .sceneCounter {
        font-size: 14px;
        color: black;
        line-height: 17px;
        padding: 20px 0 5px;
      }

      .sceneWrap {
        position: relative;
      }

      .eachPage {
        padding: 0 20px 20px;
        box-sizing: border-box;

        .description {
          font-size: 14px;
          padding: 10px 0;

          &.redAlert {
            color: red;
          }
        }

        .questionWrap {
          padding: 15px 0;

          .questionTitle {
            padding: 10px 0;

            &.redAlert {
              color: red;
            }
          }

          .questionText {
            position: relative;

            .questionArea {
              position: relative;
              top: 0;
              left: 0;
              border-radius: 5px;
              border: 1px solid #00ace2;
            }

            .textButton {
              position: absolute;
              width: 60px;
              font-weight: bold;
              bottom: 0;
              right: 0;
            }
          }
        }

        &.outRange {
          height: 10px;
          min-height: calc(100vh - 100px);
        }
      }
    }
  }
</style>

<style lang="scss">
  #pulsePage {
    .VueCarousel-pagination {
      display: none !important;
    }

    .pageDesk {
      .questionWrap {
        .questionSelect {
          .el-step {
            .el-step__title {
              color: black !important;
              line-height: normal;
              margin: 10px auto 0;
              font-size: 12px;
              word-break: break-word;
              width: calc(100% - 5px);
            }

            .el-step__icon {
              width: 40px;
              height: 40px;
              border-width: 2px;
              border-color: #00ace2;
              background-color: transparent;
              font-weight: bold;
              color: #9c9c9c;
              cursor: pointer;
            }

            // Global step head, line
            .el-step__head {
              .el-step__line {
                top: 19px;
                height: 2px;
                background: #00ace2;
                left: calc(50% + 19px);
                right: calc(-50% + 19px);
                border-radius: 10px;

                .el-step__line-inner {
                  border-width: 1px !important;
                  border-radius: 10px;
                  display: none;
                }
              }
            }

            &.redAlert {
              .el-step__icon {
                border-color: red;
              }

              .el-step__head {
                .el-step__line {
                  background: red;
                }
              }
            }

            &.noFollowingLine {
              .el-step__line {
                display: none;
              }
            }

            // Process step head, line
            .el-step__head.is-process {
              .el-step__icon {
                background-color: #00ace2;
                border-width: 4px;
                color: var(--themeFontColor);
              }

              .iconButton {
                font-weight: bold;
                color: var(--themeFontColor);
              }
            }
          }

          &.lgaStep {
            .el-step:nth-child(7) {
              .el-step__line {
                display: none;
              }
            }
          }
        }

        .questionText {
          .questionArea {
            .el-textarea__inner {
              position: relative;
              border: none;
              padding: 15px 15px 0;
              margin-bottom: 40px;
              resize: none;
            }

            .el-input__count {
              left: 0 !important;
              bottom: 0;
              border-radius: 5px;
              padding: 14px;
              right: 0;
            }
          }
        }
      }

      .customPagination {
        .el-pagination {
          display: flex;
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          justify-content: center;
          z-index: 2000;
          padding: 5px 0 15px;

          .el-pager {
            width: 81%;

            .number {
              font-size: 0;
              min-width: 1px;
              min-height: 1px;
              width: 16.8%;
            }
          }

          button {
            color: #00ace2 !important;
            padding: 0 10px;

            &.btn-prev {
              padding-left: 0;
            }

            &.btn-next {
              padding-right: 0;
            }

            &:disabled {
              color: #a1a1a1 !important;
            }

            .el-icon {
              font-size: 22px;
            }

            span {
              font-size: 16px;
              line-height: 28px;
            }
          }
        }

        .el-progress {
          left: 0;
          bottom: 0;
          width: 100%;
          z-index: 1800;
          padding: 26px 0;

          .el-progress-bar {
            width: 80%;
            margin: 0 auto;

            .el-progress-bar__outer {
              background-color: #c4c4c4;
            }
          }
        }
      }
    }

    .pageMobile {
      .eachPage {
        .questionWrap {
          .questionSelect {
            .el-radio-group {
              .el-radio-button {
                width: 100%;
                padding: 3px 0;

                &:focus {
                  outline: none !important;
                  box-shadow: none !important;
                }

                .el-radio-button__inner {
                  position: relative;
                  width: 100%;
                  padding: 15px;
                  border-radius: 5px;
                  border: 1px solid #00ace2;
                  font-size: 14px;
                  text-align: left;

                  .radioScore {
                    display: inline-block;
                    width: 22px;
                  }

                  .radioDesc {
                    display: inline-block;
                    color: black;
                    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
                  }

                  .radioActive {
                    position: absolute;
                    display: inline-block;
                    right: 10px;
                    visibility: hidden;

                    i {
                      font-size: 20px;
                    }
                  }
                }

                &.redAlert {
                  .el-radio-button__inner {
                    border: 1px solid red;
                  }
                }

                &.is-active {
                  .el-radio-button__inner {
                    background: #00ace2;

                    .radioDesc {
                      color: var(--themeFontColor);
                    }

                    .radioActive {
                      visibility: visible;
                    }
                  }
                }
              }
            }
          }

          .questionText {
            .questionArea {
              .el-textarea__inner {
                position: relative;
                border: none;
                padding: 15px 15px 0;
                margin-bottom: 40px;
                resize: none;
              }

              .el-input__count {
                left: 0 !important;
                bottom: 0;
                border-radius: 5px;
                padding: 14px;
                right: 0;
              }
            }
          }
        }
      }

      .customPagination {
        position: relative;
        top: 0;

        .el-pagination {
          display: flex;
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          justify-content: center;
          z-index: 2000;
          padding: 5px 0 15px;

          .el-pager {
            width: 85%;

            .number {
              font-size: 0;
              min-width: 1px;
              min-height: 1px;
              width: 16.8%;
            }
          }

          .btn-prev,
          .btn-next {
            color: #00ace2 !important;
            padding: 0 10px;

            &:disabled {
              color: #a1a1a1 !important;
            }

            .el-icon {
              font-size: 22px;
            }
          }
        }

        .el-progress {
          left: 0;
          bottom: 0;
          width: 100%;
          z-index: 1800;
          padding: 26px 0;
          background: white;

          .el-progress-bar {
            width: 84%;
            margin: 0 auto;
          }
        }
      }
    }
  }
</style>
